import styled from 'styled-components';
import { forwardRef, useId } from 'react';
import { useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { Controller } from 'react-hook-form';
import { useForm, useWatch } from 'react-hook-form';
import toast from 'react-hot-toast';

import { Typography } from 'design-system/src/components/Typography/Typography';
import Button from 'design-system/src/components/Button/Button';
import { RadioLabel, RadioOptionWrapper, RadioBlockGroup } from 'design-system/src/components/Radio/Radio';
import { Checkbox, CheckboxProps } from 'design-system/src/components/Checkbox/Checkbox';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage.tsx';

import {
  client,
  Drug,
  Prescriber,
  TremfyaExpressEnrollmentFormData,
  TremfyaExpressEnrollmentFormDataDefaultValues,
  TherapyTask,
} from '../../../api';
import { TremfyaEnrollmentPatientSection } from './TremfyaEnrollmentPatientSection.tsx';
import { TremfyaEnrollmentSavingsProgramSection } from './TremfyaEnrollmentSavingsProgramSection.tsx';
import { convertYesNoToBoolean, convertBooleanToYesNo, requiredErrorMessage } from '../../../utils.tsx';
import { CardSection, CustomHairline } from './sharedStyles.tsx';
import { therapyIndicationValues, TherapyIndication } from './dropdownValues.ts';

const OuterForm = styled.form`
  margin-top: 16px;
`;

const CardContainer = styled.div`
  border-radius: var(--border-radius-medium);
  border: 1px solid var(--medium-gray);
  container-name: card-container;
  container-type: inline-size;
`;

const HorizontalCardSection = styled(CardSection)`
  flex-direction: row;
  align-items: flex-start;
  padding: 16px 24px;
  margin: 0;

  // we're using the card width to determine the breakpoint because that will
  // take whether or not the sidebar is open into account
  @container card-container (width < 600px) {
    flex-direction: column;
  }
`;

const HorizontalCardLabel = styled.div`
  width: 168px;
`;

const HorizontalCardBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const CardLabelText = styled(Typography).attrs({
  styledAs: 'bodySmallDMSans',
  weight: 600,
})``;

const InnerCard = styled(CardContainer)`
  display: flex;
  padding: 16px;
  gap: 16px;
  flex-direction: column;
`;

const SubmitErrorMessageContainer = styled(CardSection)`
  margin-top: 0;
  display: flex;
  align-items: flex-end;
`;

const InlineButton = styled(Button)`
  display: inline-block;
`;

const SmallPrintSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 24px;
`;

const SubmitButtonContainer = styled(ButtonRow)`
  padding: 24px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
`;

const ForwardedCheckbox = forwardRef<HTMLButtonElement, CheckboxProps>((props, ref) => (
  <Checkbox {...props} ref={ref} />
));
const StyledCheckbox = styled(ForwardedCheckbox)`
  margin: -9px 0 0 -9px !important;
  padding-right: 12px !important;
`;

interface TremfyaExpressEnrollmentCoreFormProps {
  drug: Drug;
  prescriber?: Prescriber;
  formData: TremfyaExpressEnrollmentFormData;
  declineButton?: React.ReactNode;
  onSuccess: () => void;
  setIsSubmitting?: (isSubmitting: boolean) => void;
  submissionEndpoint: string;
}

/**
 * Core of the Tremfya Express Enrollment task form, used for both logged in and logged out views.
 */
const TremfyaExpressEnrollmentCoreForm: React.FC<TremfyaExpressEnrollmentCoreFormProps> = ({
  prescriber,
  formData,
  declineButton,
  onSuccess,
  setIsSubmitting,
  submissionEndpoint,
}) => {
  const submit = useMutation<void, Error, TremfyaExpressEnrollmentFormDataDefaultValues>({
    mutationFn: async (data) => {
      setIsSubmitting && setIsSubmitting(true);

      const formSubmissionData: Record<string, string | boolean | null> = {};

      const dataKeys = Object.keys(data);
      dataKeys.forEach((key) => {
        if (['has_existing_card', 'is_eligible'].includes(key)) {
          // Force string values back to boolean
          const value = data[key as keyof TremfyaExpressEnrollmentFormDataDefaultValues];
          formSubmissionData[key] = convertYesNoToBoolean(value as 'Yes' | 'No' | null);
        } else {
          formSubmissionData[key] = data[key as keyof TremfyaExpressEnrollmentFormDataDefaultValues];
        }
      });

      // some sections are hidden based on some answers, so the answers to the question in those sections
      // are not included in the form submission
      if (formSubmissionData.therapy_task !== TherapyTask.SavingsProgram) {
        formSubmissionData.is_eligible = null;
        if (
          ![TherapyIndication.ActivePsoriaticArthritis, TherapyIndication.ModerateToSeverePlaquePsoriasis].includes(
            formSubmissionData.therapy_indication as TherapyIndication,
          )
        ) {
          formSubmissionData.has_existing_card = null;
        }
      }
      if (!formSubmissionData.is_eligible) {
        formSubmissionData.patient_authorization = null;
        formSubmissionData.requirements_provided = null;
      }

      const res = await client.post(submissionEndpoint, {
        form_data: formSubmissionData,
      });
      return res.data;
    },
    onSuccess: () => {
      onSuccess();
      setIsSubmitting && setIsSubmitting(false);
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
      setIsSubmitting && setIsSubmitting(false);
    },
  });

  const { handleSubmit, control, formState } = useForm<TremfyaExpressEnrollmentFormDataDefaultValues>({
    defaultValues: {
      ...formData,
      has_existing_card: convertBooleanToYesNo(formData.has_existing_card),
      is_eligible: convertBooleanToYesNo(formData.is_eligible),
    },
    mode: 'onChange',
  });
  const { errors } = formState;

  const [therapyTaskValue, therapyIndicationValue, isEligibleValue] = useWatch({
    control,
    name: ['therapy_task', 'therapy_indication', 'is_eligible'],
  });

  const privacyPolicyLink = (
    <InlineButton
      variant="link"
      href="https://innovativemedicine.jnj.com/us/privacy-policy"
      target="_blank"
      rel="noopener noreferrer"
    >
      Privacy Policy
    </InlineButton>
  );

  const includeNurseGuideCheckboxId = useId();

  return (
    <>
      <Typography styledAs="bodySmallDMSans" marginBottom="24px">
        This patient may be eligible to enroll in a patient support program from Johnson & Johnson. Please complete and
        submit the form below:
      </Typography>

      <CardContainer>
        <HorizontalCardSection>
          <HorizontalCardLabel>
            <CardLabelText renderedAs="h2">Prescriber</CardLabelText>
          </HorizontalCardLabel>

          <HorizontalCardBody>
            {prescriber && <CardLabelText marginBottom="4px">{prescriber.name}</CardLabelText>}
            <Typography styledAs="bodySmallDMSans">NPI: {formData.provider_npi}</Typography>
            <Typography styledAs="bodySmallDMSans">{formData.provider_practice_name}</Typography>
            <Typography styledAs="bodySmallDMSans">{formData.provider_address}</Typography>
          </HorizontalCardBody>
        </HorizontalCardSection>
      </CardContainer>

      <OuterForm
        onSubmit={handleSubmit((data) => {
          submit.mutate(data);
        })}
      >
        <CardContainer>
          <CardSection>
            <CardLabelText renderedAs="h2">Confirm prescribed therapy</CardLabelText>
            <Typography styledAs="bodySmallDMSans">
              This form is intended to be used by a member of a patient's "Care Team", which includes individuals who
              provide care for a patient. The information you provide may be used by Johnson & Johnson Health Care
              Systems Inc., its affiliates and its service providers to enroll your patient in the Savings Program,
              initiate a benefits investigation and/or rebate request on your patient's behalf,or sign your patient up
              for other support options that may be available. In order to personalize the information the patient
              receives, the information provided may be combined with information from other sources, as described in
              our {privacyPolicyLink}. Our Privacy Policy further governs the use of the information you provide.
            </Typography>
          </CardSection>

          <CustomHairline />

          <CardSection>
            <CardLabelText renderedAs="h2">What support would you like for your patient?</CardLabelText>
            <Controller
              control={control}
              name="therapy_task"
              rules={{ required: true }}
              render={({ field }) => (
                <RadioBlockGroup>
                  <RadioOptionWrapper
                    id={useId()}
                    name="therapy_task"
                    value={TherapyTask.SavingsProgram}
                    checked={field.value === TherapyTask.SavingsProgram}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(event.target.value);
                    }}
                    hasError={!!errors.therapy_task}
                  >
                    <RadioLabel>Savings Program + Nurse Guide (Commercially-insured patients only)</RadioLabel>
                  </RadioOptionWrapper>
                  <RadioOptionWrapper
                    id={useId()}
                    name="therapy_task"
                    value={TherapyTask.NurseGuide}
                    checked={field.value === TherapyTask.NurseGuide}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(event.target.value);
                    }}
                    hasError={!!errors.therapy_task}
                  >
                    <RadioLabel>Nurse Guide only (Available to all patients)</RadioLabel>
                  </RadioOptionWrapper>
                  {errors.therapy_task && requiredErrorMessage}
                </RadioBlockGroup>
              )}
            />
          </CardSection>

          {therapyTaskValue && (
            <CardSection>
              <InnerCard>
                {therapyTaskValue === TherapyTask.SavingsProgram && (
                  <TremfyaEnrollmentSavingsProgramSection
                    control={control}
                    errors={errors}
                    isEligibleValue={isEligibleValue}
                  />
                )}

                <div>
                  <Typography styledAs="bodySmallSpaceGrotesk" renderedAs="h3" weight={500}>
                    TREMFYA withMe Nurse Guide*
                  </Typography>
                  <Typography styledAs="bodySmallSpaceGrotesk">
                    A dedicated Nurse Guide is here to help with free personalized 1-on-1 support over the phone
                    throughout your patient's treatment journey.
                  </Typography>
                </div>

                <Controller
                  control={control}
                  name="include_nurse_guide"
                  render={({ field }) => (
                    <CheckboxContainer>
                      <StyledCheckbox
                        {...field}
                        onChange={() => field.onChange(!field.value)}
                        // the user doesn't really have a choice if they've selected the nurse guide option; they're getting
                        // this by default, so we're checking the box for them
                        checked={!!field.value || therapyTaskValue === TherapyTask.NurseGuide}
                        disabled={therapyTaskValue === TherapyTask.NurseGuide}
                        id={includeNurseGuideCheckboxId}
                      />
                      <label htmlFor={includeNurseGuideCheckboxId}>
                        <Typography styledAs="bodySmallDMSans">
                          My patient would like to receive a call from a TREMFYA withMe Nurse Guide* in 1-2 business
                          days to learn more about 1-on-1 support available throughout their treatment journey. If the
                          patient wants to talk to someone immediately, they should call{' '}
                          <strong>833-WITHME1 (833-948-4631).</strong> I represent that I have authorization from the
                          patient that complies with state and federal law, and permits me to provide their information
                          for this purpose.
                        </Typography>
                        {therapyTaskValue === TherapyTask.SavingsProgram && (
                          <CardLabelText weight={600}>
                            You may opt the patient out from receiving this phone call by unchecking the box.
                          </CardLabelText>
                        )}
                      </label>
                    </CheckboxContainer>
                  )}
                />
              </InnerCard>
            </CardSection>
          )}

          <CustomHairline />

          <TremfyaEnrollmentPatientSection
            control={control}
            errors={errors}
            therapyTaskValue={therapyTaskValue as TherapyTask}
          />

          <CustomHairline />

          <CardSection>
            <CardLabelText>Confirm condition</CardLabelText>
            <Controller
              control={control}
              name="therapy_indication"
              rules={{ required: true }}
              render={({ field }) => (
                <RadioBlockGroup>
                  {therapyIndicationValues.map((value) => (
                    <RadioOptionWrapper
                      id={useId()}
                      key={value}
                      name="therapy_indication"
                      value={value}
                      checked={field.value === value}
                      onChange={() => field.onChange(value)}
                      hasError={!!errors.therapy_indication}
                    >
                      <RadioLabel>{value}</RadioLabel>
                    </RadioOptionWrapper>
                  ))}
                  {errors.therapy_indication && requiredErrorMessage}
                </RadioBlockGroup>
              )}
            />
          </CardSection>

          {therapyTaskValue === TherapyTask.SavingsProgram &&
            (therapyIndicationValue === TherapyIndication.ActivePsoriaticArthritis ||
              therapyIndicationValue === TherapyIndication.ModerateToSeverePlaquePsoriasis) && (
              <CardSection>
                <InnerCard>
                  <div>
                    <CardLabelText>Card information</CardLabelText>
                    <Typography styledAs="bodyExtraSmallSpaceGrotesk">Does the patient have a card already?</Typography>
                  </div>
                  <Controller
                    control={control}
                    name="has_existing_card"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <RadioBlockGroup>
                        <RadioOptionWrapper
                          {...field}
                          id={useId()}
                          name="has_existing_card"
                          value="Yes"
                          checked={field.value === 'Yes'}
                          onChange={() => field.onChange('Yes')}
                          hasError={!!errors.has_existing_card}
                        >
                          <RadioLabel>Yes</RadioLabel>
                        </RadioOptionWrapper>
                        <RadioOptionWrapper
                          {...field}
                          id={useId()}
                          name="has_existing_card"
                          value="No"
                          checked={field.value === 'No'}
                          onChange={() => field.onChange('No')}
                          hasError={!!errors.has_existing_card}
                        >
                          <RadioLabel>No</RadioLabel>
                        </RadioOptionWrapper>
                        {errors.has_existing_card && requiredErrorMessage}
                      </RadioBlockGroup>
                    )}
                  />
                </InnerCard>
              </CardSection>
            )}

          <SmallPrintSection>
            <Typography styledAs="bodyExtraSmallSpaceGrotesk" color="var(--dark-gray)">
              The patient support and resources provided by Tremfya withMe are not intended to give medical advice,
              replace a treatment plan from the patient's healthcare provider, offer services that would normally be
              performed by the provider's office, or serve as a reason to prescribe TREMFYA®.
            </Typography>
            <Typography styledAs="bodyExtraSmallSpaceGrotesk" color="var(--dark-gray)">
              *Nurse Guides do not provide medical advice.
            </Typography>
          </SmallPrintSection>

          <SubmitButtonContainer align={Alignment.SpaceBetween}>
            {declineButton}

            <Button type="submit" size="sm" width={!declineButton ? 'full' : 'hug'} isLoading={submit.isPending}>
              {therapyTaskValue === TherapyTask.SavingsProgram && isEligibleValue === 'No'
                ? 'Acknowledge and complete task'
                : 'Submit'}
            </Button>
          </SubmitButtonContainer>

          {!!Object.keys(errors).length && (
            <SubmitErrorMessageContainer>
              <ErrorMessage marginTop="0">Please complete the required fields</ErrorMessage>
            </SubmitErrorMessageContainer>
          )}
        </CardContainer>
      </OuterForm>
    </>
  );
};

export default TremfyaExpressEnrollmentCoreForm;
